import { DialogProps, Typography } from '@mui/material';
import { PythonFunctionSummary, QueryKey, Workflow } from 'api/model.ts';
import InfoDialog from './InfoDialog';
import { useQuery } from '@tanstack/react-query';
import { getNodesUsingFunction } from 'api/python-functions/requests';
import useRevisedWorkflowsFromNodes from 'api/workflows/useRevisedWorkflowsFromNodes';
import WorkflowCard from 'views/toasts/WorkflowCard';
import { useNavigate } from 'react-router';
import useProjects from 'api/projects/useProjects';

type Props = DialogProps & {
  pythonFunction?: PythonFunctionSummary;
  open: boolean;
  onClose: () => void;
};

const PythonFunctionUsageDialog = ({ pythonFunction, open, onClose }: Props) => {
  const navigate = useNavigate();
  const { projects } = useProjects();
  const { data: nodes, isLoading: isLoadingNodes } = useQuery({
    queryKey: [QueryKey.PythonFunctionUsage, { functionId: pythonFunction?.id }],
    queryFn: () => getNodesUsingFunction(pythonFunction?.id ?? ''),
    enabled: open && !!pythonFunction?.id,
  });
  const nodeIds = new Set(nodes?.map((node) => node.id) ?? []);
  const { revisedWorkflowsData: workflows, isLoading: isLoadingWorkflows } = useRevisedWorkflowsFromNodes({
    nodes: open ? nodes : [],
  });

  const navigateToWorkflow = (workflow: Workflow) => {
    void navigate(`/projects/${workflow.project_id}/workflows/${workflow.id}/canvas`);
  };

  if (isLoadingNodes || isLoadingWorkflows) {
    return (
      <InfoDialog
        open={open}
        maxWidth="sm"
        fullWidth
        buttonsSettings={{ confirmText: 'Close' }}
        onCancel={onClose}
        onConfirm={onClose}
      >
        <Typography>Loading...</Typography>
      </InfoDialog>
    );
  }

  return (
    <InfoDialog
      open={open}
      maxWidth="sm"
      fullWidth
      buttonsSettings={{ confirmText: 'Close' }}
      onCancel={onClose}
      onConfirm={onClose}
    >
      <Typography variant="subtitle1" className="mb-4">
        {'Workflow nodes using '}
        <Typography variant="subtitle1" component="span" fontWeight="bold">
          {pythonFunction?.name}
        </Typography>
        :
      </Typography>
      {workflows ? (
        workflows.map((workflow) => {
          const project = projects?.find((p) => p.id === workflow.project_id);
          return (
            <WorkflowCard
              key={workflow.id}
              workflow={workflow}
              project={project}
              nodeIds={nodeIds}
              onNavigate={navigateToWorkflow}
              selectable={false}
            />
          );
        })
      ) : (
        <Typography variant="body2" color="text.disabled">
          This function is not used in any workflow nodes.
        </Typography>
      )}
    </InfoDialog>
  );
};

export default PythonFunctionUsageDialog;
